import { useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, FieldLabel, Password, showSnackbar } from 'fluxo-ui';
import { BackIcon } from 'fluxo-ui/icons';

import { AuthShell, StatusCircle } from '../components/common';
import { ROUTES } from '../common/constants';
import authService from '../services/auth.service';

import type { ComponentEvent } from 'fluxo-ui';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string; token?: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    if (!token) {
        return (
            <AuthShell>
                <div className="text-center">
                    <StatusCircle tone="error" className="mx-auto mb-5" />
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Invalid reset link</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                        This password reset link is invalid or has expired. Please request a new one.
                    </p>
                    <Link to={ROUTES.FORGOT_PASSWORD}>
                        <Button label="Request New Reset Link" variant="primary" className="w-full" />
                    </Link>
                </div>
            </AuthShell>
        );
    }

    const validate = () => {
        const newErrors: { newPassword?: string; confirmPassword?: string } = {};

        if (!newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        try {
            await authService.resetPassword(token, newPassword);
            setIsSuccess(true);
            showSnackbar('Password reset successfully!', 'Success', { type: 'success' });
            setTimeout(() => navigate(ROUTES.LOGIN), 3000);
        } catch (err: any) {
            showSnackbar(err.message || 'Failed to reset password', 'Error', { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <AuthShell>
                <div className="text-center">
                    <StatusCircle tone="success" className="mx-auto mb-5" />
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Password reset!</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                        Your password has been reset successfully. Redirecting to login...
                    </p>
                    <Link to={ROUTES.LOGIN}>
                        <Button label="Go to Login" variant="primary" className="w-full" />
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <div className="text-center mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Reset password</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Enter your new password below</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <FieldLabel label="New Password" required error={errors.newPassword}>
                    <Password
                        value={newPassword}
                        onChange={(e: ComponentEvent<string>) => {
                            setNewPassword(e.value);
                            if (errors.newPassword) setErrors({ ...errors, newPassword: undefined });
                        }}
                        placeholder="Enter new password"
                        disabled={isLoading}
                        autoFocus
                    />
                </FieldLabel>

                <FieldLabel label="Confirm Password" required error={errors.confirmPassword}>
                    <Password
                        value={confirmPassword}
                        onChange={(e: ComponentEvent<string>) => {
                            setConfirmPassword(e.value);
                            if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                        }}
                        placeholder="Confirm new password"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <Button
                    type="submit"
                    label="Reset Password"
                    variant="primary"
                    className="w-full"
                    isLoading={isLoading}
                    disabled={isLoading}
                />
            </form>

            <div className="mt-6 text-center">
                <Link
                    to={ROUTES.LOGIN}
                    className="inline-flex items-center text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                >
                    <BackIcon className="w-4 h-4 mr-1" />
                    Back to login
                </Link>
            </div>
        </AuthShell>
    );
};

export default ResetPasswordPage;
